import { supabase } from './supabase';
import type { PartnerSubscription } from '../types';

const SUPABASE_URL = 'https://vhvkaisivmqxvljteyou.supabase.co';
const STRIPE_PUBLISHABLE_KEY = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_51Rh8hQFjexDxuGoTYmfMBsVn7lRhIvV8wGju3xLui51sLKbeQfA8UUuBalXhJfCktfsR9SgDtybpLC7GKyhu586100793LiD1t';

export class StripeService {
  private static readonly STRIPE_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/stripe-partner`;
  
  /**
   * Get Stripe publishable key for client-side use
   */
  static getPublishableKey(): string {
    return STRIPE_PUBLISHABLE_KEY;
  }

  /**
   * Create Stripe customer and initiate subscription checkout
   */
  static async createSubscription(partnerId: string, email: string, businessName: string): Promise<{ success: boolean; checkout_url?: string; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(this.STRIPE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          action: 'create_customer_and_subscription',
          partner_id: partnerId,
          email: email,
          business_name: businessName
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create subscription');
      }

      return result;
    } catch (error) {
      console.error('Error creating subscription:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get partner subscription status from database
   */
  static async getSubscriptionStatus(partnerId: string): Promise<{ success: boolean; subscription?: PartnerSubscription; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(this.STRIPE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          action: 'get_subscription_status',
          partner_id: partnerId
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get subscription status');
      }

      return result;
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Sync subscription data with Stripe and get updated status
   */
  static async syncAndGetSubscriptionStatus(partnerId: string): Promise<{ success: boolean; subscription?: PartnerSubscription; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(this.STRIPE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          action: 'sync_subscription_with_stripe',
          partner_id: partnerId
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to sync subscription status');
      }

      return result;
    } catch (error) {
      console.error('Error syncing subscription status:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Create customer portal session for managing subscription
   */
  static async createCustomerPortalSession(partnerId: string): Promise<{ success: boolean; portal_url?: string; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(this.STRIPE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          action: 'create_customer_portal_session',
          partner_id: partnerId
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create portal session');
      }

      return result;
    } catch (error) {
      console.error('Error creating portal session:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Check if partner has active subscription
   */
  static isSubscriptionActive(subscription: PartnerSubscription | null): boolean {
    if (!subscription) return false;
    
    // Check if subscription status is active and period is current
    const isStatusActive = subscription.subscription_status === 'active';
    const hasValidPeriod = subscription.current_period_end !== undefined && 
                          subscription.current_period_end !== null &&
                          new Date(subscription.current_period_end) > new Date();
    
    // If status is active but period has expired, we should treat it as inactive
    // This handles cases where webhooks may have failed to update the status
    return isStatusActive && hasValidPeriod;
  }

  /**
   * Get subscription status display text
   */
  static getSubscriptionStatusText(subscription: PartnerSubscription | null): string {
    if (!subscription) return 'No subscription';
    
    switch (subscription.subscription_status) {
      case 'active':
        return 'Active';
      case 'past_due':
        return 'Payment past due';
      case 'canceled':
        return 'Canceled';
      case 'unpaid':
        return 'Unpaid';
      case 'inactive':
      default:
        return 'Inactive';
    }
  }
}
